// @flow

import * as React from 'react';
import { Query } from 'react-apollo';
import { connect } from 'react-redux';
import classNames from '@ta/common.classnames';
import { featureIsEnabled } from '@ta/common.features';
import { localize } from '@ta/common.i18n';
import Button from '@ta/styleguide.button';
import { LoadingSpinner } from '@ta/styleguide.loading';
import trackEventAJAX, { Track } from '@ta/common.tracking';
import { FacebookConnectButton } from '@ta/social.facebook-button';
import ValidFacebookToken from './ValidFacebookTokenQuery.gql';
import styles from './ValidatedFacebookToken.less';

type ExternalProps = {|
  /** callback to be run if the user skips, clicks continue on the error screen, or isn't FB connected and needs to be.  */
  callback?: any => void,
  /** If isOnboarding is true:
   * - the callback will be called at start if the user is not already facebook connected.
   * - Screens will have slight styling differences (e.g. centered rather than left-aligned)
   * - There will be navigation options on each screen (e.g. 'skip for now' and 'continue')
   */
  isOnboarding?: boolean,
  children: any,
  trackingModule: string,
|};

type Props = {|
  ...ExternalProps,
  isMobile: boolean,
|};

const refetchLimit = 4;
const refetchTimeout = 1000;

/** Text shown if the user isn't facebook connected, or IS facebook connected but doesn't have a valid token set. */
const AuthScreenText = ({ isFacebookConnected }) => {
  // No valid token set
  if (isFacebookConnected) {
    return (
      <React.Fragment>
        <div className={styles.header}>
          {/* Please reconnect your Facebook account to find your friends. */}
          {localize('facebook_reconnect_header')}
        </div>
      </React.Fragment>
    );
  }
  // Not facebook connected
  return (
    <React.Fragment>
      <div className={classNames(styles.header, styles.twoLines)}>
        {/* Have friends who love to travel? */}
        {localize('have_friends_header')}
      </div>
      <div className={styles.text}>
        {/* Connect your Facebook account to follow your friends on TripAdvisor! */}
        {localize('have_friends_body')}
      </div>
    </React.Fragment>
  );
};

/**
 * Gates functionality on a valid facebook token set for the user.  If a token isn't set, a Facebook Connect button
 * will be shown instead.  Upon re-authing with facebook, the check will be reattempted.
 */
class ValidatedFacebookToken extends React.Component<Props> {
  static defaultProps = {
    callback: undefined,
    isOnboarding: false,
  };

  refetched = false;
  timesRefetched = 0;
  trackedSet = new Set<string>();

  track = (action: string) => {
    if (this.trackedSet.has(action)) {
      return;
    }
    this.trackedSet.add(action);
    trackEventAJAX({
      module: this.props.trackingModule,
      action,
      context: '',
    });
  };

  render() {
    /* Button that shows on ErrorScreen if isOnboarding is true. */
    const ErrorButton = () => {
      if (this.props.isOnboarding) {
        return (
          <Track
            className={styles.buttonContainer}
            click={{
              module: this.props.trackingModule,
              action: 'Facebooktokenmissingtryagainlater_continue_click',
              context: '',
            }}
            visible={{
              module: this.props.trackingModule,
              action: 'Facebooktokenmissingtryagainlater_shown',
              context: '',
            }}
          >
            <Button
              type="primary"
              onClick={this.props.callback}
              size={this.props.isMobile ? 'fullwidth' : 'default'}
            >
              {/* Continue */}
              {localize('button_continue')}
            </Button>
          </Track>
        );
      }
      return null;
    };

    /* Error screen shown when re-auth is attempted but token still isn't properly set afterwards. */
    const ErrorScreen = () => (
      <div
        className={classNames(styles.mainContainer, {
          [styles.onboarding]: this.props.isOnboarding,
        })}
      >
        <div className={classNames(styles.header, styles.twoLines)}>
          {/* Whoops, something went wrong. */}
          {localize('social_loading_error_1')}
        </div>
        <div className={classNames(styles.text, styles.error)}>
          {/* We're having trouble connecting to Facebook. Please try again later. */}
          {localize('facebook_reconnect_error_v2')}
        </div>
        <ErrorButton />
      </div>
    );

    /* 'skip for now' text shows on AuthScreen if isOnboarding is true. */
    const AuthSkipText = () => {
      if (this.props.isOnboarding) {
        return (
          <Track
            click={{
              module: this.props.trackingModule,
              action: 'Facebooktokenmissingconnect_skip_click',
              context: '',
            }}
            visible={{
              module: this.props.trackingModule,
              action: 'Facebooktokenmissingconnect_shown',
              context: '',
            }}
          >
            <div className={styles.hyperlink} onClick={this.props.callback}>
              {/* Skip for now */}
              {localize('onboarding_social_skip_link')}
            </div>
          </Track>
        );
      }
      return null;
    };

    /* Auth screen shows when the user is not facebook connected, or is but doesn't have a token set. */
    const AuthScreen = ({ isFacebookConnected, refetch }) => (
      <div
        className={classNames(styles.mainContainer, {
          [styles.onboarding]: this.props.isOnboarding,
        })}
      >
        <AuthScreenText isFacebookConnected={isFacebookConnected} />
        <FacebookConnectButton
          onClose={() => {
            this.track('Facebooktokenmissingconnect_continuewithfacebook_click');
            this.refetched = true;
            this.timesRefetched = 0;
            refetch().then(() => this.forceUpdate());
          }}
          fullwidth={false}
        />
        <AuthSkipText />
      </div>
    );

    const Loading = () => (
      <div className={classNames(styles.mainContainer, styles.loading)}>
        <LoadingSpinner />
      </div>
    );

    /* Disables this component's functionality if this feature is toggled on.  Just returns its children without any checks.  */
    if (featureIsEnabled('disable_fb_token_validation')) {
      return this.props.children;
    }

    return (
      <Query query={ValidFacebookToken} fetchPolicy="network-only">
        {({ loading, data, refetch }) => {
          if (loading) {
            return <Loading />;
          }
          if (!data) {
            this.track('Facebooktokenmissingconnect_fail');
            return <ErrorScreen />;
          }
          const facebookConnected =
            data &&
            data.myExternallyConnectedAccountTypes &&
            data.myExternallyConnectedAccountTypes.includes('FACEBOOK');
          // If the user isn't facebook connected and this is onboarding, call the callback.
          if (!facebookConnected && this.props.isOnboarding && this.props.callback) {
            this.props.callback();
            return null;
          }

          const tokenValid =
            data.recommendedExternalConnectionsToFollow &&
            data.recommendedExternalConnectionsToFollow.additionalInfo &&
            data.recommendedExternalConnectionsToFollow.additionalInfo.hasFacebookToken;
          if (facebookConnected) {
            // If the token isn't set, poll a number of times before showing either ErrorScreen or AuthScreen.
            if (!tokenValid && this.timesRefetched < refetchLimit) {
              this.timesRefetched++;
              // Something buggy is going on with Apollo, and the query isn't being rerendered after the refetch call is completed.
              // this is a super-hack to force it to rerender when the query completes.  It probably shouldn't stay..
              // Also, the only reason this component is written as a class is to be able to call forceUpdate.
              setTimeout(() => {
                refetch().then(() => this.forceUpdate());
              }, refetchTimeout);
              return <Loading />;
            }
            // Check if token is set properly, if so return children
            if (tokenValid) {
              if (this.refetched) {
                this.track('Facebooktokenmissingconnect_complete_success');
              }
              return this.props.children;
            }
            // Token still isn't valid and re-auth has been attempted. Fail out
            if (this.refetched) {
              this.track('Facebooktokenmissingconnect_fail');
              return <ErrorScreen />;
            }
          }
          return <AuthScreen isFacebookConnected={facebookConnected} refetch={refetch} />;
        }}
      </Query>
    );
  }
}

export default connect<Props, ExternalProps, _, _, _, _>(state => ({
  isMobile: state.meta.device.viewportCategory === 'MOBILE',
}))(ValidatedFacebookToken);
